#
# Enums
#

enum DocumentKind {
  INVALID_DOCUMENT_TYPE
  AGREEMENT_IN_PRINCIPLE
  BANK_STATEMENT
  BIRTH_CERTIFICATE
  COMPANY_ACCOUNTS
  CUSTOMER_COMMUNICATION
  DRIVERS_LICENSE_BACK
  DRIVERS_LICENSE_FRONT
  INSURANCE_APPLICATION
  GI_INSURANCE_APPLICATION
  GI_INSURANCE_ILLUSTRATION
  GI_INSURANCE_KEY_FACTS
  GI_POLICY_LETTER
  GI_STATEMENT_OF_NEEDS
  INSURANCE_ILLUSTRATION
  INSURANCE_KEY_FACTS
  INTERNAL
  INVESTMENT_STATEMENT
  LETTER_OF_GIFT
  LIVE_PHOTO
  MORTGAGE_APPLICATION
  MORTGAGE_STATEMENT
  NATIONAL_IDENTITY_CARD
  APPLICATION_LETTER
  OTHER
  P60
  PASSPORT
  PAYSLIP
  PENSION_STATEMENT
  POLICY_LETTER
  SA302
  TEMPLATE
  TEMPLATE_COMPLIANCE_EMAIL
  THIRD_PARTY_PRIVACY_NOTICE
  THIRD_PARTY_COMMUNICATION
  UTILITY_BILL
  OFFER_LETTER
  DEBT_CONSOLIDATION_COMPARISON_FORM
  DEBT_CONSOLIDATION_SUPPLEMENT
  IMPORTED_IDD
  IMPORTED_ESIS
  IMPORTED_SUITABILITY_REPORT
  CREDIT_REPORT
  AFFORDABILITY_SCREENSHOT
  PROPERTY_TITLE
  PROPERTY_TITLE_PLAN
  EXTERNAL_EVIDENCE_OF_RESEARCH
  AFFORDABILITY_CALCULATOR
  BTL_PORTFOLIO
  CONSENT
  CREDIT_CARD_STATEMENT
  CREDIT_SEARCH
  DEED_OF_POSTPONEMENT
  DIRECT_DEBIT_MANDATE
  DECLARATION_OF_TRUST
  EMPLOYMENT_CONTRACT
  EPC_REPORT
  GROUND_RENT_SERVICE_CHARGE_CONFIRMATION
  INCOME_DECLARATION
  INDEPENDENT_LEGAL_ADVICE_FORM
  INSURANCE_COMPARISON_REPORT
  COMMISSION_COMPARISON_REPORT
  INTEREST_ONLY_DECLARATION
  LAST_WILL_AND_TESTAMENT
  LEGAL_CHARGE
  LENDER_ESIS
  LENDING_INTO_RETIREMENT_DECLARATION
  LETTER_OF_EXPLANATION
  LIMITED_COMPANY_ACCOUNTS
  LOAN_SETTLEMENT_FIGURE
  MANAGEMENT_ACCOUNTS
  MEMORANDUM_OF_SALE
  MORTGAGE_QUESTIONNAIRE
  OCCUPIERS_CONSENT_FORM
  POWER_OF_ATTORNEY
  PROOF_OF_DEPOSIT
  PROPERTY_LEASE
  PURPOSE_OF_LOAN_DECLARATION
  REDEMPTION_FIGURE
  SURVEY_REPORT
  TAX_YEAR_OVERVIEW
  VALUATION
  VISA
  STATEMENT_OF_NEEDS
  CALL_RECORDING
  COST_OF_MOVE
  BUDGET_STRESS_TEST
  WELLBEING_ASSESSMENT
  INSURANCE_DECLARATION
  BENEFIT_REPORT
  RISK_INFORMATION_PACK
  EQUITY_RELEASE_FACT_FIND
  WITNESS_DECLARATION
  FEE_ELECTION
  NATIONAL_IDENTITY_CARD_FRONT
  NATIONAL_IDENTITY_CARD_BACK
  PROTECTION_ADVICE_DISCLAIMER
  CUSTOMER_DISCLOSURE_DOCUMENT
  PROTECTION_MANUAL_ADDENDUM_LETTER
  COMPLIANCE_DOCUMENT
}

enum DocumentSourceType {
  CLIENT
  GENERATED
  ONFIDO
  TRUELAYER
  TWENTY_7_TEC
  USER
  PAYMENTSHIELD
  UINSURE
  KEY
  IPIPELINE
  LAND_REGISTRY
  LENDER
  CALL_RECORDER
  SMOOVE
  SIMPLIFY
  LV
  OTHER_SOURCE
  IRESS
}

enum FileType {
  INVALID_FILE_TYPE
  HTML
  PDF
  JPG
  PNG
  DOCX
  XLSX
  PPTX
  MP3
  MP4
  WAV
  TIFF
  MBOX
  MSG
  EML
}

enum SortOrder {
  ASC
  DESC
}

enum DocumentOrderBy {
  CREATED_AT
}

enum DeleteReason {
  INVALID_DELETE_REASON
  GDPR
  AUTOMATIC_DISPOSITION
  INCORRECT_DATA
}

enum DocumentCategories {
  ALL
  DOCUMENT_REQUIREMENTS # Similar to ALL don't actually exists in BE
  ADVICE
  AFFORDABILITY
  BANK_STATEMENTS
  CREDIT_SEARCH
  COMMUNICATION
  DEBT_CONSOLIDATION
  DEPOSIT
  ID
  INCOME
  INTERNAL
  LEGAL
  LENDER_DOCUMENTS
  OTHER
  PRIVACY
  PRODUCT
  PROPERTY
  PROTECTION
  REFERENCE
  SYSTEM
  VALUATION
  DISCLOSURE
  COMPLIANCE
}

#
# Types
#

type OriginationFingerPrint {
  call_length: String
  call_originator_id: String
  call_originator_no: String
  call_originator_type: String
  call_recipient_id: String
  call_recipient_no: String
  call_recipient_type: String
  call_start: String
}

type DocumentEntity {
  # Default fields provided by Luther
  document_id: ID
  date_stored: String
  document_date: String
  type: DocumentKind
  template_type: TemplateType
  document_object_reference: String
  template_id: String
  document_signature: String
  signature_key_id: String
  source: DocumentSourceType
  source_user_id: String
  source_client_id: String
  owning_client_ids: [String!]
  document_expiry: String
  data_base64: String
  file_type: FileType
  archived: Boolean
  document_name: String
  tags: [String!]
  rendered_organisation_ids: [String!]
  rendered_user_ids: [String!]
  rendered_client_ids: [String!]
  rendered_case_ids: [String!]
  rendered_mortgage_ids: [String!]
  rendered_product_ids: [String!]
  rendered_compliance_email_recipients: [String!]
  rendered_compliance_email_subject: String
  verification_count: Int
  case_ids: [String!]
  other_type: String
  general_insurance_product_id: String
  metadata: DocumentMetadata

  # We can't have objects with arbitrarily named fields so these are omitted for now
  origination_fingerprint: OriginationFingerPrint
  # metadata_version: Object

  # Extended fields so we can access the various entities via queries
  template: Template
  source_client: Client
  source_user: User
  owning_clients(include_details: Boolean): [ClientVersion!]
  rendered_organisations: [Organisation!]
  rendered_users: [User!]
  rendered_clients: [Client!]
  rendered_cases: [Case!]
  rendered_mortgages: [Mortgage!]
  rendered_products: [MortgageProduct!]
  cases(params: GetCaseRequestInput): [CaseVersion!]
  uploaded_by: String
  verifications: [DocumentVerification!]

  signed_copy: SignedCopy

  # for caching purposes for document without an id, e.g. rendered document
  internalHash: String
}

type MIDocumentSummarySource {
  first_name: String
  id: String
  last_name: String
  type: String
}

type SignedCopy {
  data_base64: String
  date_stored: String
  file_type: FileType
  source_id: String
  source_type: String
}

type MIDocumentSummary {
  archived: Boolean
  case_ids: [String!]
  category: DocumentCategories
  client_ids: [String!]
  clients: [ClientVersion!]
  created_at: String
  data_base64: String
  date_stored: String
  document_signature: String
  delete_reason: DeleteReason
  document_id: String
  document_name: String
  file_type: FileType
  other_type: String
  rendered_mortgage_ids: [String!]
  rendered_organisation_id: String
  signed_copy: SignedCopy
  source: MIDocumentSummarySource!
  template_id: String
  template_type: TemplateType
  type: DocumentKind
  verification_count: Int
  requirement: String
  inference_result: InferenceResult
  placeholder: Boolean
  url: String
}

type MIDocumentFilters {
  bookmark: Int
  case_ids: [String!]
  categories: [String!]
  client_ids: [String!]
  include_archived: Boolean
  order_by: DocumentOrderBy
  page_size: Int
  sort_order: String
  types: [String]
}

type GetMiDocumentNextPage {
  bookmark: Int
}

type MIDocumentSummaryResponse {
  documents: [MIDocumentSummary!]
  filters: MIDocumentFilters
  next_page: GetMiDocumentNextPage
}

type MIDocumentCategory {
  categoryId: String!
  title: String!
  subTitle: String
  count: Int!
}

type MIDocumentTotal {
  count: Int!
  group_id: String!
}

input MIDocumentTotalsArgs {
  filters: MIDocumentTotalsFiltersInput
}

type MIDocumentTotalsFilters {
  case_ids: [String!]
  categories: [String!]
  client_ids: [String!]
  group_by: String
  include_archived: Boolean
  types: [String]
}

type MIDocumentTotalsResponse {
  Totals: [MIDocumentTotal!]
  Filters: MIDocumentFilters!
}

enum DocumentCaseStatus {
  UNASSIGNED
}

type DocumentMetadata {
  fees: [Fee!]
}

type DashboardDocumentsCpResponse {
  documentSummaries: [MIDocumentSummary!]!
  iddDocumentsDetails: [DocumentEntity!]!
}

#
# Inputs
#

input UploadSignedCopyInput {
  data_base64: String!
  file_type: FileType!
  source: DocumentSourceType!
}

input DocumentUploadInput {
  type: DocumentKind!
  file_type: FileType!
  other_type: String
  data_base64: String!
  document_name: String!
  source: DocumentSourceType!
  owning_client_ids: [String!]
  case_id: ID
  mortgage_id: ID
  protection_id: ID
  general_insurance_product_id: ID
  source_user_id: String
  # when sending from portal we use source_user_id
  source_client_id: String
}

input DocumentInput {
  type: DocumentKind
  other_type: String
  document_name: String
  archived: Boolean
  application_date: String
  mortgage_id: String
  protection_id: String
  general_insurance_product_id: String
  owning_client_ids: [String!]
}

input RenderAndStoreDocumentInput {
  template_name: TemplateType
  client_ids: [String!]
  mortgage_ids: [String!]
  organisation_ids: [String!]
  user_ids: [String!]
  case_ids: [String!]
  product_ids: [String!]
  verify: Boolean
}

input GetDocumentParams {
  filter_owning_client_id: String
  filter_case_id: ID
  filter_archived: Boolean
  document_details: Boolean
}

input MIDocumentSummaryFiltersInput {
  bookmark: Int
  categories: [DocumentCategories]
  client_ids: [String]
  case_ids: [String]
  include_archived: Boolean
  order_by: DocumentOrderBy
  page_size: Int
  sort_order: SortOrder
  types: [String]
}

input MIDocumentTotalsFiltersInput {
  categories: [DocumentCategories!]
  client_ids: [String!]
  include_archived: Boolean
  types: [String]
}

#
# Mistral Types
#

input MistralContextInput {
  case_id: String!
  client_names: [MistralClientInput]
}

input MistralClientInput {
  first_name: String
  last_name: String
  middle_name: String
}

input MistralInput {
  base64_encoded_document: String!
  context: MistralContextInput!
}

type MistralClient {
  first_name: String
  last_name: String
  middle_name: String
}

type MistralDocument {
  confidence_score: Float!
  explanation: String!
  type: DocumentKind!
}

type MistralResult {
  clients: [MistralClient!]!
  document: MistralDocument!
  suggested_document_name: String!
}

type MistralResponse {
  document_identifier: String!
  result: MistralResult!
}

#
# Classification Upload Document Types
#

input ClassificationUploadFullDocumentInput {
  archived: Boolean
  case_ids: [String!]
  data_base64: String
  date_stored: String
  delete_reason: DeleteReason
  document_date: String
  document_expiry: String
  document_id: String
  document_name: String
  document_object_reference: String
  document_signature: String
  file_type: FileType
  origination_fingerprint: String
  other_type: String
  owning_client_ids: [String!]
  rendered_case_ids: [String!]
  rendered_client_ids: [String!]
  rendered_compliance_email_recipients: [String!]
  rendered_compliance_email_subject: String
  rendered_mortgage_ids: [String!]
  rendered_note_ids: [String!]
  rendered_organisation_ids: [String!]
  rendered_product_ids: [String!]
  rendered_user_ids: [String!]
  signature_key_id: String
  source: DocumentSourceType
  source_client_id: String
  source_user_id: String
  tags: [String!]
  template_id: String
  template_sub_type: String
  template_type: TemplateType
  type: DocumentKind
  verification_count: Int
}

input ClassificationUploadDocumentRequestInput {
  case_ids: [String!]
  document: ClassificationUploadFullDocumentInput!
  mortgage_id: String
  protection_id: String
  general_insurance_product_id: String
}

input ClassificationUploadDocumentInput {
  document_identifier: String
  request: ClassificationUploadDocumentRequestInput!
}

type InferenceResult {
  owner_names: [MistralClient!]
  type: DocumentKind
}

type ClassificationUploadDocumentResponseDocument {
  archived: Boolean
  caseIds: [String!]
  dataBase64: String
  dateStored: String
  deleteReason: DeleteReason
  documentDate: String
  documentExpiry: String
  documentId: String
  documentName: String
  documentObjectReference: String
  documentSignature: String
  fileType: FileType
  inferenceResult: InferenceResult
  metadata: DocumentMetadata
  metadataVersion: String
  originationFingerprint: OriginationFingerPrint
  otherType: String
  owningClientIds: [String!]
  renderedCaseIds: [String!]
  renderedClientIds: [String!]
  renderedComplianceEmailRecipients: [String!]
  renderedComplianceEmailSubject: String
  renderedMortgageIds: [String!]
  renderedNoteIds: [String!]
  renderedOrganisationIds: [String!]
  renderedProductIds: [String!]
  renderedUserIds: [String!]
  signatureKeyId: String
  source: DocumentSourceType
  sourceClientId: String
  sourceUserId: String
  tags: [String!]
  templateId: String
  templateSubType: String
  templateType: TemplateType
  type: DocumentKind
  verificationCount: Int
}

type ClassificationUploadDocumentResponse {
  document: ClassificationUploadDocumentResponseDocument
  retry_with_base64: Boolean
}
